import { useLayoutEffect, useState, type ReactNode } from 'react';

export const SLIDE_WIDTH = 1280;
export const SLIDE_HEIGHT = 960;

// The card occupies at most 95% of the available area (viewport minus
// leftGutter on the horizontal axis, full viewport height on the vertical),
// leaving a dark-blue gutter on every side. CSS `zoom: N` (not
// `transform: scale`) applied to the fixed 1280×960 box preserves the 4:3
// aspect ratio AND reflows/re-rasterizes content at the final size — so SVG
// logos stay crisp instead of being bitmap-scaled. `Math.min(sx, sy)` picks
// the more-constrained axis so neither dimension breaches the 95% bound.
export const VIEWPORT_FILL = 0.95;

function useSlideScale(leftGutter: number) {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const compute = () => {
      const availableW = Math.max(0, window.innerWidth - leftGutter);
      const availableH = window.innerHeight;
      const sx = (availableW * VIEWPORT_FILL) / SLIDE_WIDTH;
      const sy = (availableH * VIEWPORT_FILL) / SLIDE_HEIGHT;
      setScale(Math.max(0, Math.min(sx, sy)));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [leftGutter]);

  return scale;
}

export function SlideFrame({
  children,
  leftGutter = 0,
  cornerSlot,
}: {
  children: ReactNode;
  leftGutter?: number;
  cornerSlot?: ReactNode;
}) {
  const scale = useSlideScale(leftGutter);

  return (
    <div
      className="pointer-events-none absolute top-0 bottom-0 right-0 flex items-center justify-center overflow-hidden"
      style={{ left: leftGutter }}
    >
      <div
        className="pointer-events-auto relative overflow-hidden rounded-lg bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]"
        style={{
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          zoom: scale,
          flexShrink: 0,
        }}
      >
        {children}
        {cornerSlot ? <div className="absolute right-6 top-5 z-30">{cornerSlot}</div> : null}
      </div>
    </div>
  );
}
